import { useMemo } from 'react';
import checkPermission from '../../../../../utils/helpers/checkPermission';
import {
  ROLE_PHD_FIELD_COMMISSION_WORKING_GROUP_CHAIR,
  ROLE_PHD_EXTERNAL_SUPERVISOR,
  ROLE_PHD_FIELD_COMMISSION_WORKING_GROUP_MEMBER,
  ROLE_PHD_STUDENT,
  ROLE_PHD_SUPERVISOR,
} from '../../../../../utils/constants/roles.js';

const hasRole = (user, ...roleNames) =>
  Boolean(user?.roles?.some((role) => roleNames.includes(role?.name)));

/**
 * Derives every role/permission flag and per-row action predicate used by the
 * PhD theses table. Row predicates prefer the backend-provided `can_*` flag and
 * fall back to a client-side decision so the UI degrades gracefully.
 *
 * @param {object} user Authenticated user from the auth slice.
 * @param {boolean} isAdmin Result of `useIsUserPhdAdmin()`.
 * @param {(key: string) => string} t i18n translator, used for tooltips.
 */
const usePhdThesisPermissions = (user, isAdmin, t) => {
  const isChair = hasRole(user, ROLE_PHD_FIELD_COMMISSION_WORKING_GROUP_CHAIR);
  const isTeacher = hasRole(
    user,
    ROLE_PHD_SUPERVISOR,
    ROLE_PHD_EXTERNAL_SUPERVISOR,
  );
  const isPhdSupervisor = isTeacher;
  const isStudent = hasRole(user, ROLE_PHD_STUDENT);
  const isCommissionMember = hasRole(
    user,
    ROLE_PHD_FIELD_COMMISSION_WORKING_GROUP_MEMBER,
  );

  const isPhdAdminByPermission = useMemo(
    () => checkPermission(user, 'phd-system-states.store', false),
    [user],
  );
  const canApproveTheses = isChair || isAdmin || isPhdAdminByPermission;
  const canSeePendingFilter = canApproveTheses || isCommissionMember;
  const shouldScopePendingCount = canSeePendingFilter;
  const isChairOnly = isChair && !isAdmin && !isTeacher;
  const isChairTeacher = isChair && isTeacher && !isAdmin;
  const restrictStatusFilters =
    isStudent && !isAdmin && !isChair && !isCommissionMember;
  const canEditTheses = useMemo(
    () => checkPermission(user, 'phd-theses.update', isAdmin),
    [user, isAdmin],
  );
  const canCreateTheses = useMemo(
    () => checkPermission(user, 'phd-theses.store', isAdmin),
    [user, isAdmin],
  );
  const canDeleteTheses = useMemo(
    () => checkPermission(user, 'phd-theses.destroy', isAdmin),
    [user, isAdmin],
  );
  const canViewTheses = useMemo(
    () =>
      checkPermission(user, 'phd-theses.show', isAdmin) ||
      checkPermission(user, 'phd-theses.index', isAdmin),
    [user, isAdmin],
  );
  const canCreateComment = useMemo(
    () => checkPermission(user, 'phd-comments.store', isAdmin),
    [user, isAdmin],
  );
  const canEditPendingTheses =
    !isCommissionMember || isAdmin || isChair || isTeacher;
  const canArchiveRestore = canEditTheses && !isChairOnly;
  const currentUserPersonId = user?.person_id ?? user?.id ?? null;

  const isOwnSupervisor = (row) =>
    Boolean(
      row?.phd_supervisor_id &&
        currentUserPersonId &&
        Number(row.phd_supervisor_id) === Number(currentUserPersonId),
    );
  const backendFlag = (row, key) =>
    typeof row?.[key] === 'boolean' ? row[key] : null;

  const canDeleteRow = (row) => {
    const serverDecision = backendFlag(row, 'can_delete');
    if (serverDecision !== null) {
      return serverDecision;
    }
    if (!row || !canDeleteTheses) {
      return false;
    }
    if (isTeacher && !isAdmin && !isChair && !isOwnSupervisor(row)) {
      return false;
    }
    return !(isChairTeacher && !isOwnSupervisor(row));
  };

  const canEditRow = (row) => {
    const serverDecision = backendFlag(row, 'can_edit');
    if (serverDecision !== null) {
      return serverDecision;
    }
    if (!row || !canEditTheses) {
      return false;
    }
    if (row?.archived_at) {
      return false;
    }
    if (!canEditPendingTheses && !row?.approved_at && !row?.archived_at) {
      return false;
    }
    if (isTeacher && !isAdmin && !isChair && !isOwnSupervisor(row)) {
      return false;
    }
    if (isChairOnly) {
      return !row?.approved_at;
    }
    if (isChairTeacher && row?.approved_at) {
      return isOwnSupervisor(row);
    }
    return true;
  };

  const canApproveRow = (row) => {
    const serverDecision = backendFlag(row, 'can_approve');
    if (serverDecision !== null) {
      return serverDecision;
    }
    if (!row || !canApproveTheses) {
      return false;
    }
    const unresolvedCommentsCount = row?.unresolved_comments_count || 0;
    if (unresolvedCommentsCount > 0) {
      return false;
    }
    if (isAdmin || isPhdAdminByPermission) {
      return true;
    }
    if (isChair && isTeacher) {
      return !isOwnSupervisor(row);
    }
    return true;
  };

  const canArchiveRow = (row) => {
    const serverDecision = backendFlag(row, 'can_archive');
    if (serverDecision !== null) {
      return serverDecision;
    }
    if (!row || !canArchiveRestore || !row?.archived_at || row?.defended_at) {
      return false;
    }
    if (isTeacher && !isAdmin && !isChair && !isOwnSupervisor(row)) {
      return false;
    }
    if (isChairTeacher) {
      return isOwnSupervisor(row);
    }
    return true;
  };

  const canDefendRow = (row) => {
    const serverDecision = backendFlag(row, 'can_defend');
    if (serverDecision !== null) {
      return serverDecision;
    }
    if (!row || !canArchiveRestore) {
      return false;
    }
    if (!row?.approved_at || row?.archived_at || row?.defended_at) {
      return false;
    }
    if (!row?.phd_student_id) {
      return false;
    }
    if (isTeacher && !isAdmin && !isChair && !isOwnSupervisor(row)) {
      return false;
    }
    if (isChairTeacher) {
      return isOwnSupervisor(row);
    }
    return true;
  };

  const canRestoreRow = (row) => {
    const serverDecision = backendFlag(row, 'can_restore');
    if (serverDecision !== null) {
      return serverDecision;
    }
    if (!row || !canArchiveRestore) {
      return false;
    }
    if (isTeacher && !isAdmin && !isChair && !isOwnSupervisor(row)) {
      return false;
    }
    if (isChairTeacher) {
      return isOwnSupervisor(row);
    }
    return true;
  };

  const canDenyRow = (row) => {
    const serverDecision = backendFlag(row, 'can_deny');
    if (serverDecision !== null) {
      return serverDecision;
    }
    if (!row) {
      return false;
    }
    if (isAdmin || isPhdAdminByPermission) {
      return true;
    }
    if (!isChair) {
      return false;
    }
    if (isTeacher) {
      return !isOwnSupervisor(row);
    }
    return true;
  };

  const editDisabledTooltip = (row) => {
    if (!row) {
      return t('Upraviť nie je dostupné');
    }
    if (isChairOnly && row?.approved_at) {
      return t('Predseda pracovnej skupiny môže upravovať iba čakajúce témy.');
    }
    if (isTeacher && !isAdmin && !isChair && !isOwnSupervisor(row)) {
      return t('Ako školiteľ môžete upravovať len vlastné témy.');
    }
    if (isChairTeacher && row?.approved_at && !isOwnSupervisor(row)) {
      return t('Ako školiteľ môžete upravovať len vlastné témy.');
    }
    return t('Upraviť nie je dostupné');
  };

  const canCopyRow = (row) => {
    const serverDecision = backendFlag(row, 'can_duplicate');
    if (serverDecision !== null) {
      return serverDecision;
    }
    if (!row || !isPhdSupervisor || !canCreateTheses || !row?.defended_at) {
      return false;
    }
    return isOwnSupervisor(row);
  };

  const canCreateCommentRow = (row) => {
    const serverDecision = backendFlag(row, 'can_comment');
    if (serverDecision !== null) {
      return serverDecision;
    }
    return Boolean(canCreateComment && row?.status_code === 'pending');
  };

  return {
    isChair,
    isTeacher,
    isPhdSupervisor,
    isStudent,
    isCommissionMember,
    isPhdAdminByPermission,
    canApproveTheses,
    canSeePendingFilter,
    shouldScopePendingCount,
    isChairOnly,
    isChairTeacher,
    restrictStatusFilters,
    canEditTheses,
    canCreateTheses,
    canDeleteTheses,
    canViewTheses,
    canCreateComment,
    canEditPendingTheses,
    canArchiveRestore,
    currentUserPersonId,
    isOwnSupervisor,
    backendFlag,
    canDeleteRow,
    canEditRow,
    canApproveRow,
    canArchiveRow,
    canDefendRow,
    canRestoreRow,
    canDenyRow,
    editDisabledTooltip,
    canCopyRow,
    canCreateCommentRow,
  };
};

export default usePhdThesisPermissions;
